if (!customElements.get('reservation-variant-picker')) {
  const SPANISH_MONTHS = [
    'enero',
    'febrero',
    'marzo',
    'abril',
    'mayo',
    'junio',
    'julio',
    'agosto',
    'septiembre',
    'octubre',
    'noviembre',
    'diciembre',
  ];

  const SPANISH_WEEKDAYS = ['lun', 'mar', 'mie', 'jue', 'vie', 'sab', 'dom'];

  customElements.define(
    'reservation-variant-picker',
    class ReservationVariantPicker extends HTMLElement {
      constructor() {
        super();
        this.initAttempts = 0;
      }

      connectedCallback() {
        if (!this.variantChangeUnsubscriber && typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, this.handleVariantChange.bind(this));
        }
        if (this.initializing || this.initialized) return;
        this.scheduleInitialize();
      }

      disconnectedCallback() {
        this.variantChangeUnsubscriber?.();
        this.variantChangeUnsubscriber = null;
        this.removeEventBindings();
        this.initialized = false;
        this.initializing = false;
      }

      scheduleInitialize(force = false) {
        if (force) {
          this.initialized = false;
          this.removeEventBindings();
        }
        this.initializing = true;
        requestAnimationFrame(() => this.initialize());
      }

      handleVariantChange() {
        if (!this.isConnected) return;

        const latestVariantSelects =
          this.closest('variant-selects') || document.getElementById(this.dataset.variantSelectsId);

        if (latestVariantSelects && latestVariantSelects !== this.variantSelects) {
          this.scheduleInitialize(true);
          return;
        }

        this.syncStateFromNative();
        this.render();
      }

      initialize() {
        this.initializing = false;
        if (!this.isConnected || this.initialized) return;

        this.variantSelects = this.closest('variant-selects') || document.getElementById(this.dataset.variantSelectsId);
        if (!this.variantSelects) return this.retryInitialize();
        this.renderMode = this.dataset.renderMode || 'full';

        if (this.renderMode === 'details' && this.shouldUseNativeFallback()) {
          this.nativeSelects = Array.from(this.variantSelects.querySelectorAll('.reservation-variant-picker__native select'));
          this.dateSelectNative = this.nativeSelects[Number(this.dataset.dateOptionPosition) - 1];
          this.turnSelectNative = this.nativeSelects[Number(this.dataset.timeOptionPosition) - 1];
          this.restoreNativeDateOptions();
          this.classList.add('reservation-variant-picker--native-fallback');
          this.initialized = true;
          this.initAttempts = 0;
          return;
        }

        this.nativeSelects = Array.from(this.variantSelects.querySelectorAll('.reservation-variant-picker__native select'));
        this.dateSelectNative = this.nativeSelects[Number(this.dataset.dateOptionPosition) - 1];
        this.turnSelectNative = this.nativeSelects[Number(this.dataset.timeOptionPosition) - 1];

        this.data = this.parseData();
        if (!this.data || !Array.isArray(this.data.variants) || !this.dateSelectNative || !this.turnSelectNative) {
          return this.retryInitialize();
        }

        this.todayDate = this.startOfDay(new Date());
        this.todayKey = this.formatDateKey(this.todayDate);
        this.variants = this.normalizeVariants(this.data.variants);
        if (!this.variants.length) return;

        this.dateSelect = this.querySelector('[data-reservation-date]');
        this.turnSelect = this.querySelector('[data-reservation-turn]');
        this.monthLabel = this.querySelector('[data-calendar-month]');
        this.weekdaysRoot = this.querySelector('[data-calendar-weekdays]');
        this.gridRoot = this.querySelector('[data-calendar-grid]');

        this.buildMaps();

        const initialVariant =
          this.variants.find((variant) => String(variant.id) === String(this.data.selectedVariantId)) || this.variants[0];
        const initialDate =
          this.dateSelectNative?.value && this.dateMap.has(this.dateSelectNative.value)
            ? this.dateSelectNative.value
            : initialVariant.date;
        const initialTurn = this.resolveTurnForDate(initialDate, this.turnSelectNative?.value || initialVariant.turn);
        const initialResolvedVariant = this.findVariant(initialDate, initialTurn) || initialVariant;
        this.selectedDate = initialDate;
        this.selectedTurn = initialTurn;
        this.currentMonth = this.startOfMonth(this.parseIsoDate(initialDate) || initialVariant.dateObject);

        this.applySelectionToNative(initialDate, initialTurn, { dispatch: false });
        this.syncProductFormState(initialResolvedVariant);
        this.syncStateFromNative();

        this.renderWeekdays();
        this.bindEvents();
        this.render();
        this.classList.add('is-ready');
        this.initialized = true;
        this.initAttempts = 0;
      }

      retryInitialize() {
        this.initAttempts += 1;
        if (this.initAttempts > 10) return;
        this.scheduleInitialize();
      }

      parseData() {
        try {
          return JSON.parse(this.querySelector('[data-reservation-variant-data]')?.textContent || '{}');
        } catch (error) {
          console.error('[reservation-picker] invalid json', error);
          return null;
        }
      }

      normalizeVariants(variants) {
        const normalizedVariants = variants
          .map((variant) => {
            const dateObject = this.parseIsoDate(variant.date);
            if (!dateObject || !variant.turn) return null;

            return {
              id: variant.id,
              available: Boolean(variant.available),
              date: variant.date,
              turn: variant.turn,
              price: variant.price || '',
              dateObject,
              sortMinutes: this.extractMinutes(variant.turn),
            };
          })
          .filter(Boolean)
          .sort((left, right) => {
            if (left.date === right.date) return left.sortMinutes - right.sortMinutes;
            return left.date.localeCompare(right.date);
          });

        const upcomingVariants = normalizedVariants.filter((variant) => variant.dateObject >= this.todayDate);
        return upcomingVariants.length ? upcomingVariants : normalizedVariants;
      }

      bindEvents() {
        this.removeEventBindings();

        this.handleDateSelectChange = this.handleDateSelectChange || ((event) => {
          event.stopPropagation();
          this.applySelectionToNative(this.dateSelect.value, this.turnSelectNative?.value || this.selectedTurn);
        });

        this.handleTurnSelectChange = this.handleTurnSelectChange || ((event) => {
          event.stopPropagation();
          this.applySelectionToNative(this.dateSelectNative?.value || this.selectedDate, this.turnSelect.value);
        });

        this.handleNativeChange = this.handleNativeChange || (() => {
          if (this.isSyncingNative) return;
          this.syncStateFromNative();
          this.render();
        });

        this.handlePrevClick = this.handlePrevClick || (() => {
          this.currentMonth = new Date(
            Date.UTC(this.currentMonth.getUTCFullYear(), this.currentMonth.getUTCMonth() - 1, 1)
          );
          this.renderCalendar();
        });

        this.handleNextClick = this.handleNextClick || (() => {
          this.currentMonth = new Date(
            Date.UTC(this.currentMonth.getUTCFullYear(), this.currentMonth.getUTCMonth() + 1, 1)
          );
          this.renderCalendar();
        });

        this.handleTodayClick = this.handleTodayClick || (() => {
          const targetDate = this.dateMap.has(this.todayKey) ? this.todayKey : this.sortedDates[0];
          if (!targetDate) return;
          this.applySelectionToNative(targetDate, this.turnSelectNative?.value || this.selectedTurn);
        });

        this.prevButton = this.querySelector('[data-calendar-prev]');
        this.nextButton = this.querySelector('[data-calendar-next]');
        this.todayButton = this.querySelector('[data-calendar-today]');

        this.dateSelect?.addEventListener('change', this.handleDateSelectChange);
        this.turnSelect?.addEventListener('change', this.handleTurnSelectChange);
        this.dateSelectNative?.addEventListener('change', this.handleNativeChange);
        this.turnSelectNative?.addEventListener('change', this.handleNativeChange);
        this.prevButton?.addEventListener('click', this.handlePrevClick);
        this.nextButton?.addEventListener('click', this.handleNextClick);
        this.todayButton?.addEventListener('click', this.handleTodayClick);
      }

      removeEventBindings() {
        this.dateSelect?.removeEventListener('change', this.handleDateSelectChange);
        this.turnSelect?.removeEventListener('change', this.handleTurnSelectChange);
        this.dateSelectNative?.removeEventListener('change', this.handleNativeChange);
        this.turnSelectNative?.removeEventListener('change', this.handleNativeChange);
        this.prevButton?.removeEventListener('click', this.handlePrevClick);
        this.nextButton?.removeEventListener('click', this.handleNextClick);
        this.todayButton?.removeEventListener('click', this.handleTodayClick);
      }

      render() {
        this.syncStateFromNative();
        this.buildMaps();

        if (!this.dateMap.size) return;

        if (!this.dateMap.has(this.selectedDate)) {
          this.selectedDate = this.sortedDates[0];
        }

        this.selectedTurn = this.resolveTurnForDate(this.selectedDate, this.selectedTurn);

        if (this.dateSelect) this.renderDateSelect();
        if (this.turnSelect) this.renderTurnSelect();
        if (this.monthLabel && this.gridRoot) this.renderCalendar();
      }

      buildMaps() {
        this.dateMap = new Map();

        this.variants.forEach((variant) => {
          if (!this.dateMap.has(variant.date)) this.dateMap.set(variant.date, []);
          this.dateMap.get(variant.date).push(variant);
        });

        this.sortedDates = Array.from(this.dateMap.keys()).sort((left, right) => left.localeCompare(right));
      }

      renderDateSelect() {
        this.dateSelect.innerHTML = this.sortedDates
          .map((date) => {
            const selected = date === this.selectedDate ? ' selected="selected"' : '';

            return `<option value="${this.escapeHtml(date)}"${selected}>${this.escapeHtml(this.formatLongDate(date))}</option>`;
          })
          .join('');
        this.dateSelect.value = this.selectedDate;
      }

      renderTurnSelect() {
        const turns = this.dateMap.get(this.selectedDate) || [];
        const nextTurn = this.resolveTurnForDate(this.selectedDate, this.selectedTurn);
        this.selectedTurn = nextTurn;

        this.turnSelect.innerHTML = turns
          .map((variant) => {
            const selected = variant.turn === nextTurn ? ' selected="selected"' : '';
            const disabled = variant.available ? '' : ' disabled';
            const suffix = variant.available ? '' : ' - Agotado';

            return `<option value="${this.escapeHtml(variant.turn)}"${selected}${disabled}>${this.escapeHtml(
              `${variant.turn}${suffix}`
            )}</option>`;
          })
          .join('');
        this.turnSelect.value = this.selectedTurn;
      }

      renderCalendar() {
        this.monthLabel.textContent = this.formatMonthYear(this.currentMonth);

        const monthYear = this.currentMonth.getUTCFullYear();
        const monthIndex = this.currentMonth.getUTCMonth();
        const firstDay = new Date(Date.UTC(monthYear, monthIndex, 1));
        const startOffset = (firstDay.getUTCDay() + 6) % 7;
        const daysInMonth = new Date(Date.UTC(monthYear, monthIndex + 1, 0)).getUTCDate();
        const cells = [];

        for (let day = 1; day <= daysInMonth; day += 1) {
          const dateObject = new Date(Date.UTC(monthYear, monthIndex, day));
          const dateKey = this.formatDateKey(dateObject);
          const variants = this.dateMap.get(dateKey) || [];
          const availableCount = variants.filter((variant) => variant.available).length;
          const dayClasses = ['reservation-picker__day'];
          const columnStart = day === 1 && startOffset > 0 ? ` style="grid-column-start: ${startOffset + 1};"` : '';

          if (dateKey === this.selectedDate) dayClasses.push('is-selected');
          if (dateKey === this.todayKey) dayClasses.push('is-today');

          const turnsMarkup = variants
            .map((variant) => {
              const selectedClass = dateKey === this.selectedDate && variant.turn === this.selectedTurn ? ' is-selected' : '';
              const disabled = variant.available ? '' : ' disabled';

              return `<button type="button" class="reservation-picker__turn${selectedClass}" data-date="${this.escapeHtml(
                dateKey
              )}" data-turn="${this.escapeHtml(variant.turn)}"${disabled}>${this.escapeHtml(variant.turn)}</button>`;
            })
            .join('');

          cells.push(`
            <div class="${dayClasses.join(' ')}"${columnStart}>
              <div class="reservation-picker__day-head">
                <button
                  type="button"
                  class="reservation-picker__day-number"
                  data-date="${this.escapeHtml(dateKey)}"
                  ${variants.length ? '' : 'disabled'}
                >
                  ${day}
                </button>
                <span class="reservation-picker__day-count">${availableCount ? `${availableCount} turno${availableCount > 1 ? 's' : ''}` : ''}</span>
              </div>
              <div class="reservation-picker__turns">${turnsMarkup}</div>
            </div>
          `);
        }

        this.gridRoot.innerHTML = cells.join('');
        this.bindCalendarButtons();
      }

      bindCalendarButtons() {
        this.gridRoot.querySelectorAll('[data-date]').forEach((button) => {
          button.addEventListener('click', () => {
            const date = button.dataset.date;
            const turn = button.dataset.turn || this.turnSelectNative?.value || this.selectedTurn;
            if (!date) return;

            this.applySelectionToNative(date, turn);
          });
        });
      }

      renderWeekdays() {
        if (!this.weekdaysRoot) return;

        const weekdays = SPANISH_WEEKDAYS.map((label) => {
          return `<div class="reservation-picker__weekday">${this.escapeHtml(label)}</div>`;
        });

        this.weekdaysRoot.innerHTML = weekdays.join('');
      }

      resolveTurnForDate(date, preferredTurn) {
        const variants = this.dateMap.get(date) || [];
        if (!variants.length) return '';

        const preferred = variants.find((variant) => variant.turn === preferredTurn && variant.available);
        if (preferred) return preferred.turn;

        const firstAvailable = variants.find((variant) => variant.available);
        if (firstAvailable) return firstAvailable.turn;

        return variants[0].turn;
      }

      findVariant(date, turn) {
        return this.variants.find((variant) => variant.date === date && variant.turn === turn) || null;
      }

      syncProductFormState(variant) {
        const productInfo = this.closest('product-info');
        const sectionId = productInfo?.dataset?.section;
        if (!productInfo || !sectionId || !variant) return;

        productInfo
          .querySelectorAll(`#product-form-${sectionId}, #product-form-installment-${sectionId}`)
          .forEach((productForm) => {
            const input = productForm.querySelector('input[name="id"]');
            if (!input) return;
            input.value = variant.id;
            input.disabled = false;
          });

        const productFormElement = productInfo.querySelector('product-form');
        if (productFormElement?.toggleSubmitButton) {
          productFormElement.toggleSubmitButton(!variant.available, window.variantStrings.soldOut);
        }

        if (variant.available) {
          productFormElement?.submitButton?.removeAttribute('aria-disabled');
        }
      }

      applySelectionToNative(date, preferredTurn, { dispatch = true } = {}) {
        const nextDate = date && this.dateMap.has(date) ? date : this.sortedDates[0];
        if (!nextDate) return;

        const nextTurn = this.resolveTurnForDate(nextDate, preferredTurn || this.turnSelectNative?.value || this.selectedTurn);
        const nextVariant = this.findVariant(nextDate, nextTurn);

        this.isSyncingNative = true;
        this.setSelectValue(this.dateSelectNative, nextDate);
        this.setSelectValue(this.turnSelectNative, nextTurn);
        if (dispatch) {
          this.dispatchNativeChange(this.turnSelectNative);
        }
        this.isSyncingNative = false;
        this.syncProductFormState(nextVariant);
        this.syncStateFromNative();
      }

      syncStateFromNative() {
        const fallbackDate = this.dateMap.has(this.selectedDate) ? this.selectedDate : this.sortedDates[0];
        const nativeDate = this.dateSelectNative?.value;
        const nextDate = nativeDate && this.dateMap.has(nativeDate) ? nativeDate : fallbackDate;

        if (!nextDate) return;

        this.selectedDate = nextDate;
        this.selectedTurn = this.resolveTurnForDate(nextDate, this.turnSelectNative?.value || this.selectedTurn);

        const nextDateObject = this.parseIsoDate(this.selectedDate);
        if (nextDateObject) {
          this.currentMonth = this.startOfMonth(nextDateObject);
        }
      }

      setSelectValue(select, nextValue) {
        if (!select) return;

        let matchedIndex = -1;
        Array.from(select.options).forEach((option) => {
          const isMatch = option.value === nextValue;
          option.selected = isMatch;
          if (isMatch) {
            matchedIndex = option.index;
            option.setAttribute('selected', 'selected');
          } else {
            option.removeAttribute('selected');
          }
        });

        if (matchedIndex >= 0) {
          select.selectedIndex = matchedIndex;
        }
        select.value = nextValue;
      }

      dispatchNativeChange(element) {
        if (!element) return;

        if (typeof Event === 'function') {
          element.dispatchEvent(new Event('change', { bubbles: true }));
          return;
        }

        const legacyEvent = document.createEvent('Event');
        legacyEvent.initEvent('change', true, false);
        element.dispatchEvent(legacyEvent);
      }

      shouldUseNativeFallback() {
        const userAgent = window.navigator.userAgent || '';
        const platform = window.navigator.platform || '';
        const maxTouchPoints = Number(window.navigator.maxTouchPoints || 0);
        const isIOSDevice = /iPad|iPhone|iPod/.test(userAgent) || (platform === 'MacIntel' && maxTouchPoints > 1);
        const isWebKit = /WebKit/i.test(userAgent);
        const isExcludedBrowser = /CriOS|FxiOS|EdgiOS|OPiOS/i.test(userAgent);

        return isIOSDevice && isWebKit && !isExcludedBrowser;
      }

      restoreNativeDateOptions() {
        if (!this.dateSelectNative?.options?.length) return;

        Array.from(this.dateSelectNative.options).forEach((option) => {
          option.textContent = this.formatLongDate(option.value);
        });
      }

      parseIsoDate(value) {
        if (!value || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return null;
        const [year, month, day] = value.split('-').map(Number);
        return new Date(Date.UTC(year, month - 1, day));
      }

      startOfMonth(date) {
        return new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), 1));
      }

      startOfDay(date) {
        return new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
      }

      formatDateKey(date) {
        const year = date.getUTCFullYear();
        const month = `${date.getUTCMonth() + 1}`.padStart(2, '0');
        const day = `${date.getUTCDate()}`.padStart(2, '0');
        return `${year}-${month}-${day}`;
      }

      formatLongDate(value) {
        const date = this.parseIsoDate(value);
        if (!date) return value;

        return `${date.getUTCDate()} de ${SPANISH_MONTHS[date.getUTCMonth()]} de ${date.getUTCFullYear()}`;
      }

      formatMonthYear(date) {
        return `${SPANISH_MONTHS[date.getUTCMonth()]} de ${date.getUTCFullYear()}`;
      }

      extractMinutes(label) {
        const match = String(label).match(/(\d{1,2}):(\d{2})/);
        if (!match) return 0;
        return Number(match[1]) * 60 + Number(match[2]);
      }

      escapeHtml(value) {
        return String(value)
          .split('&')
          .join('&amp;')
          .split('<')
          .join('&lt;')
          .split('>')
          .join('&gt;')
          .split('"')
          .join('&quot;')
          .split("'")
          .join('&#39;');
      }
    }
  );
}
